import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../hooks/useTheme';
import api from '../../api/api';
import { Building2, CheckCircle } from 'lucide-react';

export default function EmployerApply() {
    const { isDarkMode } = useTheme();
    const [form, setForm] = useState({
        companyName: '',
        jobTitle: '',
        companyWebsite: '',
        additionalNotes: '',
    });
    const [errors, setErrors] = useState({});
    const [submitted, setSubmitted] = useState(false);
    const [alreadyApplied, setAlreadyApplied] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.companyName.trim()) {
            setErrors({ companyName: 'Company name is required.' });
            return;
        }
        setErrors({});
        try {
            await api.post('/employer/apply', {
                company_name: form.companyName,
                job_title: form.jobTitle || undefined,
                company_website: form.companyWebsite || undefined,
                additional_notes: form.additionalNotes || undefined,
            }, { withCredentials: true });
            setSubmitted(true);
        } catch (err) {
            if (err.response?.status === 409) {
                setAlreadyApplied(true);
            } else {
                setErrors({ server: err.response?.data?.error || 'Failed to submit application.' });
            }
        }
    };

    const cardClass = `rounded-2xl border p-8 shadow-lg ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-100'}`;

    if (alreadyApplied) {
        return (
            <div className="max-w-lg mx-auto mt-12">
                <div className={cardClass}>
                    <div className="text-center space-y-4">
                        <div className={`w-14 h-14 rounded-full flex items-center justify-center mx-auto ${isDarkMode ? 'bg-amber-900/40' : 'bg-amber-50'}`}>
                            <Building2 size={26} className="text-amber-500" />
                        </div>
                        <h2 className="text-xl font-bold">Application Already Submitted</h2>
                        <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            You've already submitted an employer application. Our team will review it and get back to you.
                        </p>
                        <Link to="/userdashboard" className="inline-block mt-2 text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors">
                            Back to Dashboard
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    if (submitted) {
        return (
            <div className="max-w-lg mx-auto mt-12">
                <div className={cardClass}>
                    <div className="text-center space-y-4">
                        <div className={`w-14 h-14 rounded-full flex items-center justify-center mx-auto ${isDarkMode ? 'bg-primary-900/40' : 'bg-primary-50'}`}>
                            <CheckCircle size={26} className="text-primary-500" />
                        </div>
                        <h2 className="text-xl font-bold">Application Submitted!</h2>
                        <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            We'll review your application and reach out to you. In the meantime, you can continue using Pono Pilot as usual.
                        </p>
                        <Link to="/userdashboard" className="inline-block mt-2 text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors">
                            Back to Dashboard
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-lg mx-auto mt-6">
            <div className={cardClass}>
                <div className="flex items-center gap-3 mb-6">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDarkMode ? 'bg-primary-900/40' : 'bg-primary-50'}`}>
                        <Building2 size={20} className="text-primary-600" />
                    </div>
                    <div>
                        <h1 className="text-xl font-bold">Apply as an Employer</h1>
                        <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Tell us about your company to get started</p>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5">
                    <div>
                        <label className={`text-sm font-semibold mb-1 block ${isDarkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                            Company Name <span className="text-red-500">*</span>
                        </label>
                        <input
                            name="companyName"
                            value={form.companyName}
                            onChange={handleChange}
                            placeholder="Acme Corp"
                            className={`w-full rounded-xl border p-3 bg-transparent transition-colors text-sm ${
                                errors.companyName
                                    ? 'border-red-500'
                                    : (isDarkMode ? 'border-slate-700 text-slate-100 placeholder:text-slate-600' : 'border-slate-300 text-slate-900 placeholder:text-slate-400')
                            }`}
                        />
                        {errors.companyName && <p className="text-red-500 text-xs mt-1 font-medium">{errors.companyName}</p>}
                    </div>

                    <div>
                        <label className={`text-sm font-semibold mb-1 block ${isDarkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                            Your Job Title <span className={`font-normal text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>(optional)</span>
                        </label>
                        <input
                            name="jobTitle"
                            value={form.jobTitle}
                            onChange={handleChange}
                            placeholder="e.g. HR Manager, Recruiter"
                            className={`w-full rounded-xl border p-3 bg-transparent transition-colors text-sm ${isDarkMode ? 'border-slate-700 text-slate-100 placeholder:text-slate-600' : 'border-slate-300 text-slate-900 placeholder:text-slate-400'}`}
                        />
                    </div>

                    <div>
                        <label className={`text-sm font-semibold mb-1 block ${isDarkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                            Company Website <span className={`font-normal text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>(optional)</span>
                        </label>
                        <input
                            name="companyWebsite"
                            value={form.companyWebsite}
                            onChange={handleChange}
                            placeholder="https://yourcompany.com"
                            className={`w-full rounded-xl border p-3 bg-transparent transition-colors text-sm ${isDarkMode ? 'border-slate-700 text-slate-100 placeholder:text-slate-600' : 'border-slate-300 text-slate-900 placeholder:text-slate-400'}`}
                        />
                    </div>

                    <div>
                        <label className={`text-sm font-semibold mb-1 block ${isDarkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                            Additional Notes <span className={`font-normal text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>(optional)</span>
                        </label>
                        <textarea
                            name="additionalNotes"
                            value={form.additionalNotes}
                            onChange={handleChange}
                            rows={3}
                            placeholder="Anything you'd like us to know about your hiring needs..."
                            className={`w-full rounded-xl border p-3 bg-transparent transition-colors resize-none text-sm ${isDarkMode ? 'border-slate-700 text-slate-100 placeholder:text-slate-600' : 'border-slate-300 text-slate-900 placeholder:text-slate-400'}`}
                        />
                    </div>

                    {errors.server && <p className="text-red-500 text-sm font-medium">⚠️ {errors.server}</p>}

                    <button
                        type="submit"
                        className="w-full bg-primary-600 text-white py-3 rounded-xl font-bold hover:bg-primary-700 transition-all shadow-lg active:scale-[0.98]"
                    >
                        Submit Application
                    </button>
                </form>
            </div>
        </div>
    );
}
